import endpoints from '@app/src/api/endpoints'
import { FetchKey } from '@app/src/api/fetchHooks'
import { useUpdateResource } from '@app/src/api/updateHooks'
import Select from '@app/src/components/Form/Select/ControlledSelect'
import LoadingButton from '@app/src/components/LoadingButton'
import { useSnackbar } from '@app/src/context/SnackbarContext'
import useErrorNotification from '@app/src/hooks/errorNotification'
import { categoryTypeOrder, useQuestionCategories } from '@app/src/hooks/useQuestionCategories'
import type { QuestionCategoryApi, QuestionCategoryType } from '@app/src/types/resourceExplorer'
import { NotificationSeverity } from '@app/src/wf-constants'
import { Grid, TextField, Typography } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { CATEGORY_TYPE_OPTIONS } from './AddCategory'

interface RenameCategoryFormData {
  id?: number
  name: string
  categoryType: QuestionCategoryType
}

const RenameCategory: React.FC = () => {
  const { control, register, handleSubmit, watch, setValue } = useForm<RenameCategoryFormData>()
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const { mutateAsync: updateCategoryAsync, isPending: isUpdatingCategory } = useUpdateResource<
    QuestionCategoryApi,
    RenameCategoryFormData
  >()
  const { showSnackbar } = useSnackbar()
  const { showErrorNotification } = useErrorNotification()

  const categoryId = watch('id')
  const categoryName = watch('name')
  const categoryType = watch('categoryType')

  const allFieldsFilled = Boolean(categoryId) && categoryName?.length > 0 && Boolean(categoryType)

  const { categories } = useQuestionCategories()
  const options = categories.map(category => ({
    label: category.name,
    value: category.id,
    additionalText: category.categoryType,
  }))

  useEffect(() => {
    if (categoryId) {
      setValue('name', '')
      setValue(
        'categoryType',
        categories.find(category => category.id === categoryId)?.categoryType as QuestionCategoryType,
      )
    }
  }, [categoryId, setValue])

  const onSubmit = async (formData: RenameCategoryFormData) => {
    if (!formData.id) return

    await updateCategoryAsync(
      {
        url: endpoints.questionCategory,
        body: { id: formData.id, name: formData.name, categoryType: formData.categoryType },
      },
      {
        onSuccess: () => {
          showSnackbar({ message: 'The category was renamed', severity: NotificationSeverity.success })
          setValue('id', undefined)
          setValue('name', '')
          setValue('categoryType', undefined)
          queryClient.invalidateQueries({
            predicate: query => query.queryKey.includes(FetchKey.QuestionCategories),
          })
        },
        onError: error => {
          if (error.statusCode === 400 || error.statusCode === 409)
            showSnackbar({ message: error.body?.message, severity: NotificationSeverity.error })
          else showErrorNotification({ requestError: error })
        },
      },
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="subtitle1" mb={3}>
        Rename category
      </Typography>
      <Grid container spacing={1}>
        <Grid item md={3} xs={12}>
          <Select<number, QuestionCategoryApi>
            control={control}
            variant="outlined"
            name="id"
            fieldLabel="Category to rename"
            findSelectedValue={(value, option) => value === option.value}
            groupBy={option => `${option.additionalText}`}
            options={options}
            customSort={(a, b) => {
              const aIndex = categoryTypeOrder.indexOf(a.additionalText as QuestionCategoryType)
              const bIndex = categoryTypeOrder.indexOf(b.additionalText as QuestionCategoryType)
              return aIndex - bIndex
            }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <TextField
            variant="outlined"
            sx={{ width: '100%', height: '100%', '& .MuiInputBase-root': { height: '100%' } }}
            inputRef={register()}
            label="New category name"
            name="name"
            inputProps={{ maxLength: 60 }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <Select
            control={control}
            variant="outlined"
            name="categoryType"
            fieldLabel="Category type"
            options={CATEGORY_TYPE_OPTIONS}
            required
            rules={{ required: true }}
          />
        </Grid>
        <Grid item md={3} xs={12}>
          <LoadingButton
            variant="contained"
            type="submit"
            disabled={!allFieldsFilled}
            loading={isUpdatingCategory}
            sx={{ height: '100%', width: '50%' }}
          >
            {formatMessage({ id: 'general.save' })}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  )
}

export default RenameCategory
